import { Injectable, Injector } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MicrofrontendProcessCreatedEvent, MicrofrontendProcessType, OpenFileDocumentEvent } from "./microfrontend-events.model";
import { ExportProcessSnackComponent } from "src/app/modules/shared/export-process-snack/export-process-snack.component";
import { FileDocumentViewModalComponent } from "src/app/modules/shared/file-document-view-modal/file-document-view-modal.component";
import { OrganizationalUnitService } from "src/app/modules/shared/services/organizational-unit.service";
import { MicrofrontendOuChangeRequestEvent } from "../routing/ou-events.model";

/**
 * Service that listens to microfrontend events.
 */
@Injectable({
  providedIn: "root",
})
export class MicrofrontendEventsService {

  private static readonly processCreatedMessageKeys: Record<MicrofrontendProcessType, string> = {
    "notify-pending-documentation": "documentSearch.exportProcessSnack.notifyPendingMsg"
  };

  // In the future, this service should not rely on dependencies like MatDialog directly.
  // Instead, it should notify other parts of the application to handle the events accordingly.
  // Remove this dependency
  constructor(
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly injector: Injector
  ) {}
  /**
   * Loads and initializes the listener for microfrontend events.
   * Called once during application initialization via APP_INITIALIZER.
   */
  load(): void {
    this.listenForFileDocumentDialog();
    this.listenForProcessCreated();
    this.listenForOuChangeRequest();
  }

  /**
   * Listens to the 'open-file-document' event dispatched by the microfrontend.
   * Opens the FileDocumentViewModalComponent with the provided configuration.
   */
  private listenForFileDocumentDialog(): void {
    globalThis.addEventListener(
      "open-file-document",
      (event: CustomEvent<OpenFileDocumentEvent>) => {
        const { config } = event.detail;

        if (config?.data) {
          this.dialog.open(FileDocumentViewModalComponent, config);
        }
      }
    );
  }

  /**
   * Listens to the 'microfrontend-process-created' event dispatched by the microfrontend.
   * Shows the shared export-process snackbar ("Ver progreso") for the created process and cancels
   * the event (preventDefault) so the microfrontend knows the host handled the confirmation.
   * An invalid payload is left uncancelled, so the microfrontend keeps its own message.
   */
  private listenForProcessCreated(): void {
    globalThis.addEventListener(
      MicrofrontendProcessCreatedEvent.eventType,
      (rawEvent: Event) => {
        const { processOrchestratorId, processType } = (rawEvent as MicrofrontendProcessCreatedEvent).detail ?? {};
        const messageKey = Object.hasOwn(MicrofrontendEventsService.processCreatedMessageKeys, processType)
          ? MicrofrontendEventsService.processCreatedMessageKeys[processType]
          : undefined;
        if (typeof processOrchestratorId !== "string" || !processOrchestratorId.trim() || !messageKey) {
          return;
        }

        this.snackBar.openFromComponent(ExportProcessSnackComponent, {
          duration: 10000,
          horizontalPosition: "left",
          verticalPosition: "bottom",
          panelClass: "export-process-snack-panel",
          data: { processOrchestratorId, messageKey }
        });
        rawEvent.preventDefault();
      }
    );
  }

  /**
   * Listens to the 'microfrontend-ou-change-request' event dispatched by the microfrontend.
   * The host owns the current OU: it applies the change and broadcasts HostOuChangeEvent back.
   */
  private listenForOuChangeRequest(): void {
    globalThis.addEventListener(
      MicrofrontendOuChangeRequestEvent.eventType,
      (rawEvent: Event) => {
        const { ou } = (rawEvent as MicrofrontendOuChangeRequestEvent).detail ?? {};
        if (ou?.id == null) {
          return;
        }

        const organizationalUnitService = this.injector.get(OrganizationalUnitService);
        if (organizationalUnitService.getCurrentOU()?.id !== ou.id) {
          organizationalUnitService.setCurrentOU(ou);
        }
      }
    );
  }
}
